import { Button } from '@/components/UI/Common/Admin/Button';
import { ImageUpload } from '@/components/UI/Common/Admin/ImageUpload';
import { Input } from '@/components/UI/Common/Admin/Input';
import { MultiSelect } from '@/components/UI/Common/Admin/MultiSelect';
import { Select } from '@/components/UI/Common/Admin/Select';
import { chatScheme } from '@/config/validations';
import { useGetList } from '@/queries/admin';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';

export type FormData = z.infer<typeof chatScheme>;

export const Form = ({
  formData,
  onSubmit,
  onDelete,
}: {
  onSubmit: (data: FormData) => void;
  formData?: Partial<FormData>;
  onDelete?: VoidFunction;
}) => {
  const { data: userGroups } = useGetList('user-groups');
  const { data: chatGroups } = useGetList('chat-groups');

  const initialFormState = formData
    ? formData
    : ({
        title: '',
        systemName: '',
        type: 'public',
        userId: '',
        sort: 1,
        userGroups: [],
        chatGroups: [],
        avatar: {
          url: '',
          rawFile: null,
        },
      } as Partial<FormData>);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
    control,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(chatScheme),
    defaultValues: initialFormState,
    mode: 'onChange',
  });

  const selectedType = watch('type');

  const userGroupsOptions = useMemo(
    () => userGroups?.data.map((group) => ({ label: group.name, value: group.id })) ?? [],
    [userGroups],
  );
  const chatGroupsOptions = useMemo(
    () => chatGroups?.data.map((group) => ({ label: group.title, value: group.id })) ?? [],
    [chatGroups],
  );
  const typeOptions = useMemo(
    () => [
      { label: 'Публичный', value: 'public' },
      { label: 'Персональный', value: 'personal' },
    ],
    [],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
      <Input {...register('title')} error={errors?.title?.message} label="Название" required />
      <Input
        {...register('systemName')}
        error={errors?.systemName?.message}
        label="Системное имя"
        required
      />
      <Controller
        name="type"
        control={control}
        render={({ field, fieldState }) => (
          <Select
            error={fieldState.error?.message}
            label="Тип"
            options={typeOptions}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            required
          />
        )}
      />
      <Input
        {...register('userId')}
        error={errors?.userId?.message}
        label="User ID"
        placeholder={selectedType === 'public' ? 'Для публичного чата оставьте пустым' : ''}
      />
      <Input
        {...register('sort', { valueAsNumber: true })}
        error={errors?.sort?.message}
        label="Порядок"
        required
        type="number"
        min={1}
      />
      <Controller
        name="userGroups"
        control={control}
        render={({ field, fieldState }) => (
          <MultiSelect
            error={fieldState.error?.message}
            label="Группы пользователей"
            options={userGroupsOptions}
            value={field.value || []}
            onChange={(options) => field.onChange(options)}
            placeholder="Выберите группы пользователей"
          />
        )}
      />
      <Controller
        name="chatGroups"
        control={control}
        render={({ field, fieldState }) => (
          <MultiSelect
            error={fieldState.error?.message}
            label="Группы чатов"
            options={chatGroupsOptions}
            value={field.value || []}
            onChange={(options) => field.onChange(options)}
            placeholder="Выберите группы чатов"
          />
        )}
      />
      <Controller
        name="avatar"
        control={control}
        render={({ field, fieldState }) => (
          <ImageUpload
            error={fieldState.error?.message}
            value={field.value?.url}
            onImageChange={(file) => field.onChange({ url: '', rawFile: file })}
          />
        )}
      />

      <div className="flex justify-end gap-4">
        {onDelete && (
          <Button variant="danger" onClick={onDelete} type="button">
            Удалить
          </Button>
        )}
        <Button variant="secondary" disabled={!isDirty} onClick={() => reset()} type="button">
          Сбросить
        </Button>
        <Button disabled={!isValid || !isDirty} type="submit">
          Сохранить
        </Button>
      </div>
    </form>
  );
};
